/**
 * Content for the Modernist presentation.
 *
 * The design file carries its own hardcoded arrays, but they were authored
 * before LeetSync, FrameX and the AI Medical Chatbot were added — porting them
 * verbatim would have silently dropped three projects and the Chrome extension.
 * So the design's *presentation* is ported and the site's real content is kept.
 *
 * Shapes follow the design (short + description, stats as plain strings) so the
 * ported markup can consume them directly.
 */

export const profile = {
  name: 'Devesh Samant',
  first: 'Devesh',
  last: 'Samant',
  kicker: 'B.Tech CSE — Dehradun, India',
  email: 'aani64257@gmail.com',
  phone: '+91 7820020955',
  location: 'Dehradun, Uttarakhand, India',
  resume: '/documents/Devesh_Samant_Resume.pdf',
  photo: '/images/myphoto.jpg',
  intro:
    'Computer Science graduate building machine-learning models, web apps and desktop tools. I care about systems that are fast, legible and useful — the same things a good grid cares about.',
  mission:
    "As a B.Tech Computer Science graduate from Graphic Era Hill University, I'm dedicated to mastering the art of programming and technology. My mission is to create innovative solutions that make a positive impact while continuously learning in this ever-evolving field.",
};

export const roles = [
  'Full Stack Developer',
  'Machine Learning Engineer',
  'Android Developer',
  'Problem Solver',
];

export const stack = ['C++', 'Python', 'React', 'Node', 'ML'];

export const metrics = [
  { value: '10', label: 'Projects shipped' },
  { value: '5', label: 'Apps published' },
  { value: '7.58', label: 'CGPA' },
  { value: '3+', label: 'Years coding' },
];

/** The design renders these as a mock terminal on the home page. */
export const terminal = [
  { prompt: '$', text: 'whoami' },
  { out: 'devesh — cse graduate, builds things that ship' },
  { prompt: '$', text: 'ls ~/focus' },
  { out: 'machine-learning/  web/  android/  desktop/' },
  { prompt: '$', text: 'cat status.txt' },
  { out: 'open to work — reply time measured in hours' },
];

export const bars = [
  { label: 'Python', value: 90 },
  { label: 'C++', value: 85 },
  { label: 'React', value: 80 },
  { label: 'Node.js', value: 75 },
  { label: 'Machine Learning', value: 80 },
  { label: 'Kotlin / Android', value: 70 },
];

export const aboutFacts = [
  { k: 'Degree', v: 'B.Tech Computer Science' },
  { k: 'University', v: 'Graphic Era Hill University' },
  { k: 'Based in', v: 'Dehradun, Uttarakhand' },
  { k: 'Focus', v: 'ML · Web · Android' },
];

export const timeline = [
  {
    period: '2021 — 2025',
    title: 'B.Tech Computer Science',
    org: 'Graphic Era Hill University',
    metric: '7.58 CGPA',
    body:
      "Completed a Bachelor's in Computer Science and Engineering in 2025, building a foundation in programming, algorithms, data structures and software engineering.",
    tags: ['CSE', 'Algorithms', 'Programming'],
  },
  {
    period: '2020 — 2021',
    title: 'Class 12th (Science)',
    org: 'N.P.S Khatima',
    metric: '87%',
    body:
      'Higher secondary in Physics, Chemistry and Mathematics — where the analytical habits behind everything since were formed.',
    tags: ['PCM', 'Science'],
  },
  {
    period: '2018 — 2019',
    title: 'Class 10th',
    org: 'N.P.S Khatima',
    metric: '88%',
    body:
      'Secondary education with strong mathematics and science fundamentals that later carried into programming.',
    tags: ['Mathematics', 'Foundation'],
  },
];

/* The design renders each skill as a labelled bar, so items carry a level.
   Numbers are the ones the original About page already published. */
export const skillGroups = [
  {
    title: 'Languages',
    items: [
      { name: 'Python', pct: 90 },
      { name: 'C++', pct: 85 },
      { name: 'JavaScript', pct: 75 },
      { name: 'Java', pct: 70 },
    ],
  },
  {
    title: 'Web',
    items: [
      { name: 'HTML5', pct: 95 },
      { name: 'CSS3', pct: 90 },
      { name: 'React', pct: 80 },
      { name: 'Node.js', pct: 75 },
    ],
  },
  {
    title: 'Machine learning',
    items: [
      { name: 'ML', pct: 80 },
      { name: 'Data Science', pct: 75 },
      { name: 'OpenCV', pct: 70 },
    ],
  },
  {
    title: 'Mobile',
    items: [
      { name: 'React Native', pct: 75 },
      { name: 'Android', pct: 70 },
      { name: 'Flutter', pct: 65 },
      { name: 'iOS', pct: 60 },
    ],
  },
  {
    title: 'Data & tools',
    items: [
      { name: 'VS Code', pct: 90 },
      { name: 'Git', pct: 80 },
      { name: 'MySQL', pct: 75 },
      { name: 'MongoDB', pct: 70 },
    ],
  },
  {
    title: 'Cloud & desktop',
    items: [
      { name: 'AWS', pct: 75 },
      { name: 'Docker', pct: 70 },
      { name: 'Jenkins', pct: 70 },
      { name: 'Kubernetes', pct: 65 },
    ],
  },
];

/* Nothing here is a website, so there is no Web filter: LeetSync is a browser
   extension, and FrameX, the To-Do Manager and FileOrganizer are desktop
   applications that happen to be built with web technology. */
export const filters = [
  { id: 'all', label: 'All' },
  { id: 'ml', label: 'Machine Learning' },
  { id: 'desktop', label: 'Desktop Apps' },
  { id: 'mobile', label: 'Mobile' },
  { id: 'extension', label: 'Extensions' },
  { id: 'ai', label: 'AI & Vision' },
];

export const categoryLabel = {
  ml: 'Machine Learning',
  desktop: 'Desktop App',
  mobile: 'Mobile App',
  extension: 'Browser Extension',
  ai: 'AI & Computer Vision',
};

export const projects = [
  {
    id: 'leetsync',
    title: 'LeetSync',
    category: 'extension',
    image: '/images/leetsync.webp',
    short: 'Chrome extension that auto-pushes every accepted LeetCode solution to GitHub.',
    description:
      'Chrome extension (Manifest V3) that watches LeetCode submissions and, the moment one is accepted, pushes the code and full problem description to GitHub. Organises everything into a clean folder structure, generates a per-problem README with badges, and maintains a live progress dashboard. 15+ languages; the token never leaves Chrome sync storage.',
    tech: ['JavaScript', 'Chrome MV3', 'GitHub API', 'GraphQL'],
    stats: ['Fully automatic', '15+ languages'],
    github: 'https://github.com/Deveshsamant/LeetSync',
    demo: 'https://chromewebstore.google.com/detail/neghhaodkpiafoalaeldhbnboncnalec?utm_source=item-share-cb',
  },
  {
    id: 'framex',
    title: 'FrameX',
    category: 'desktop',
    image: '/images/framex.webp',
    short: 'Tauri and Rust media player with locally generated AI subtitles.',
    description:
      'Desktop media player built with Tauri, React and Vite, pairing a Rust backend with a glassmorphic dark UI. Playback runs on libmpv. Generates English subtitles locally with OpenAI Whisper — no cloud keys — builds thumbnails via FFmpeg, and fetches posters from TMDB only for Movies and Anime folders.',
    tech: ['Tauri', 'Rust', 'TypeScript', 'React'],
    stats: ['Local AI subtitles', 'Native performance'],
    github: 'https://github.com/Deveshsamant/FrameX---Media-Player',
    demo: 'https://github.com/Deveshsamant/Devesh/releases/download/installers-v1/FrameX.zip',
  },
  {
    id: 'medical-chatbot',
    title: 'AI Medical Chatbot',
    category: 'ml',
    image: '/images/medical-chatbot.webp',
    short: 'Llama 2 7B fine-tuned on 120k medical Q&A pairs with QLoRA, on a 6GB GPU.',
    description:
      'End-to-end QLoRA pipeline fine-tuning Llama 2 7B on ~120,000 medical Q&A pairs from HealthCareMagic-100k, iCliniq and a custom set. 4-bit quantisation, gradient checkpointing and a paged 8-bit optimiser bring a ~14GB model down to about 4GB so it trains on a 6GB RTX 3050.',
    tech: ['Python', 'PyTorch', 'QLoRA', 'Transformers'],
    stats: ['120k Q&A pairs', 'Trains on 6GB VRAM'],
    github: 'https://github.com/Deveshsamant/Ai-Medical-Chatbot',
    demo: 'https://www.kaggle.com/datasets/deveshsamant/chat-medic',
  },
  {
    id: 'mobile',
    title: 'Mobile Price Prediction',
    category: 'ml',
    image: '/images/mobile.jpg',
    short: 'Random Forest model pricing phones at 92% accuracy from their spec sheet.',
    description:
      'Machine learning model using the Random Forest algorithm to predict mobile phone prices with 92% accuracy, analysing RAM, storage, camera specs and brand.',
    tech: ['Python', 'Scikit-learn', 'Pandas', 'Streamlit'],
    stats: ['92% accuracy', '10K+ records'],
    github: 'https://github.com/Deveshsamant/Mobile-range-Prediction-using-Random-Forest',
    demo: 'https://mobile-price.streamlit.app/',
  },
  {
    id: 'hand',
    title: 'Hand Gesture Recognition',
    category: 'ai',
    image: '/images/hand.jpg',
    short: 'Real-time gesture detection with OpenCV and MediaPipe.',
    description:
      'Real-time hand gesture recognition using OpenCV and MediaPipe. Detects and classifies hand gestures for interactive applications and accessibility features.',
    tech: ['Python', 'OpenCV', 'MediaPipe', 'NumPy'],
    stats: ['Real-time', '10+ gestures'],
    github: '#',
    demo: '#',
  },
  {
    id: 'movie',
    title: 'Movie Recommendation System',
    category: 'ml',
    image: '/images/movie.jpg',
    short: 'Content-based filtering over 5000+ titles using cosine similarity.',
    description:
      'Content-based filtering recommendation system using cosine similarity, analysing movie features and user preferences to suggest personalised recommendations, served through Flask.',
    tech: ['Python', 'Pandas', 'Scikit-learn', 'Flask'],
    stats: ['5000+ movies', 'High accuracy'],
    github: '#',
    demo: '#',
  },
  {
    id: 'todo',
    title: 'Project To-Do Manager',
    category: 'desktop',
    image: '/images/Project-To-Do-Manager.png',
    short: 'Electron desktop app for per-project task boards with drag and drop.',
    description:
      'Desktop application built with Electron.js for organising tasks within different projects. Features drag-and-drop and dark/light themes.',
    tech: ['Electron.js', 'JavaScript', 'HTML', 'CSS'],
    stats: ['Task management', 'Drag & drop'],
    github: 'https://github.com/Deveshsamant/Project-To-Do-Manager',
    demo: '#',
  },
  {
    id: 'dockeep',
    title: 'DocKeep',
    category: 'mobile',
    image: '/images/DocKeep.png',
    short: 'Kotlin Android app storing personal documents entirely on-device.',
    description:
      'Android application built with Kotlin for storing and organising personal documents locally. Material Design with dark/light themes.',
    tech: ['Kotlin', 'XML', 'Room Database', 'Glide'],
    stats: ['Document storage', 'Android app'],
    github: 'https://github.com/Deveshsamant/DocKeep',
    demo: '#',
  },
  {
    id: 'fileorganizer',
    title: 'FileOrganizer',
    category: 'desktop',
    image: '/images/FileOrganizer.png',
    short: 'Threaded Python tool that sorts and organises a Windows drive.',
    description:
      'Desktop application built with Python for organising and managing files on Windows. Features file browsing and advanced sorting.',
    tech: ['Python', 'Tkinter', 'FFmpeg', 'Threading'],
    stats: ['File management', 'Advanced sorting'],
    github: 'https://github.com/Deveshsamant/FileOrganizer',
    demo: '#',
  },
];

/*
 * Installers are GitHub Release assets, not files in this repo.
 *
 * They are 220 MB between them; carrying that in git makes every clone pay
 * for it, and Vercel does not fetch Git LFS objects, so an LFS copy would
 * deploy as pointer files and every download button would serve 133 bytes.
 * Release assets have neither problem.
 */
export const apps = [
  {
    id: 'leetsync',
    title: 'LeetSync',
    platform: 'Chrome Extension',
    type: 'Extension',
    version: '1.0.0',
    size: 'Web Store',
    image: '/images/leetsync.webp',
    description:
      'Watches your LeetCode submissions and pushes every accepted solution to GitHub — code, problem description, per-problem README and a live dashboard.',
    features: ['Fully automatic', '15+ languages', 'Live dashboard', 'Private & local'],
    href: 'https://chromewebstore.google.com/detail/neghhaodkpiafoalaeldhbnboncnalec?utm_source=item-share-cb',
    cta: 'Add chrome extension',
    external: true,
  },
  {
    id: 'framex',
    title: 'FrameX Media Player',
    platform: 'Windows',
    type: 'ZIP',
    version: '1.0.0',
    size: '95.5 MB',
    image: '/images/framex.webp',
    description:
      'Portable build of the Tauri media player — unzip and run. Bundles libmpv. AI subtitles additionally need Python, FFmpeg and openai-whisper on your PATH.',
    features: ['Portable', 'libmpv playback', 'Local AI subtitles', 'TMDB posters'],
    href: 'https://github.com/Deveshsamant/Devesh/releases/download/installers-v1/FrameX.zip',
    cta: 'Download ZIP',
    external: true,
  },
  {
    id: 'projecttodo',
    title: 'Project To-Do Manager',
    platform: 'Windows',
    type: 'EXE',
    version: '1.0.0',
    size: '72.5 MB',
    image: '/images/Project-To-Do-Manager.png',
    description:
      'Electron desktop app for organising tasks within projects, with drag-and-drop and dark/light themes.',
    features: ['Task management', 'Drag & drop', 'Project organisation', 'Theming'],
    href: 'https://github.com/Deveshsamant/Devesh/releases/download/installers-v1/Project.To-Do.Manager.Setup.1.0.0.exe',
    cta: 'Download EXE',
    external: true,
  },
  {
    id: 'fileorganizer',
    title: 'FileOrganizer',
    platform: 'Windows',
    type: 'EXE',
    version: '1.0.0',
    size: '34.6 MB',
    image: '/images/FileOrganizer.png',
    description:
      'Python desktop tool for organising and managing files on Windows, with browsing and advanced sorting.',
    features: ['File management', 'Advanced sorting', 'File browsing', 'Windows integration'],
    href: 'https://github.com/Deveshsamant/Devesh/releases/download/installers-v1/FileOrganizer.exe',
    cta: 'Download EXE',
    external: true,
  },
  {
    id: 'dockeep',
    title: 'DocKeep',
    platform: 'Android',
    type: 'APK',
    version: '1.0.0',
    size: '9.4 MB',
    image: '/images/DocKeep.png',
    description:
      'Android app for storing and organising personal documents locally, with Material Design and theming.',
    features: ['Document storage', 'Material Design', 'Dark/light themes', 'Local storage'],
    href: 'https://github.com/Deveshsamant/Devesh/releases/download/installers-v1/DocKeep.apk',
    cta: 'Download APK',
    external: true,
  },
];

export const certs = [
  {
    id: 'python',
    title: 'Crash Course on Python',
    issuer: 'Google & Coursera',
    year: '2024',
    hours: '40 hours',
    image: '/images/python.jpg',
    description:
      'Intensive course on Python fundamentals covering data structures, functions and object-oriented programming.',
    tech: ['Python', 'Programming', 'Data Structures'],
    verify: 'https://www.coursera.org/verify/LES49221JBAH',
    download: '/python.pdf',
  },
  {
    id: 'web',
    title: 'The Web Developer Bootcamp 2024',
    issuer: 'Udemy',
    year: '2024',
    hours: '60+ hours',
    image: '/images/web.jpg',
    description:
      'Full-stack web development covering front-end and back-end technologies, databases and deployment.',
    tech: ['JavaScript', 'React', 'Node.js', 'MongoDB'],
    verify: 'https://www.udemy.com/certificate/UC-a3a88382-d387-4a2e-93e2-c707ea7586d6/',
    download: '/web.pdf',
  },
  {
    id: 'kotlin',
    title: 'Android & Kotlin Development Masterclass',
    issuer: 'Udemy',
    year: '2024',
    hours: '50+ hours',
    image: '/images/kotlin.jpg',
    description:
      'Android app development with Kotlin, covering UI design, data persistence, networking and deployment.',
    tech: ['Kotlin', 'Android Studio', 'Mobile', 'UI/UX'],
    verify: 'https://www.udemy.com/certificate/UC-c05bfd05-f93c-4876-9b84-7fd8f10dc039/',
    download: '/kotlin.pdf',
  },
];

export const channels = [
  { label: 'Email', value: 'aani64257@gmail.com', href: 'mailto:aani64257@gmail.com' },
  { label: 'Phone', value: '+91 7820020955', href: 'tel:+917820020955' },
  { label: 'GitHub', value: 'Deveshsamant', href: 'https://github.com/Deveshsamant' },
  { label: 'LinkedIn', value: 'devesh-samant', href: 'https://www.linkedin.com/in/devesh-samant-b78376258/' },
  { label: 'LeetCode', value: 'deveshsamant1744', href: 'https://leetcode.com/u/deveshsamant1744/' },
  { label: 'Location', value: 'Dehradun, India' },
];

export const social = [
  { label: 'GitHub', href: 'https://github.com/Deveshsamant' },
  { label: 'LinkedIn', href: 'https://www.linkedin.com/in/devesh-samant-b78376258/' },
  { label: 'LeetCode', href: 'https://leetcode.com/u/deveshsamant1744/' },
  { label: 'Instagram', href: 'https://www.instagram.com/devesh.samant/' },
  { label: 'Twitter', href: 'https://x.com/DeveshSama32978?t=3ZEb0YjtYfHMk30saT-AXA&s=09' },
  { label: 'WhatsApp', href: 'https://wa.me/917820020955' },
];

/** Matches the `themes` array in the design's logic block. */
export const themeSwatches = [
  { id: 'modernist-bone', name: 'Bone', ground: '#f3f2f2', accent: '#ec3013' },
  { id: 'modernist-ink', name: 'Ink', ground: '#201e1d', accent: '#ec3013' },
  { id: 'modernist-blueprint', name: 'Blueprint', ground: '#eae9e9', accent: '#ae1800' },
];

export const isLive = (href) => Boolean(href) && href !== '#';
